import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ClipboardPaste, BellRing, X, Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { parsePartsPaste } from "./bomUtils";
import { AssemblyIcon } from "./BomBits";

const API = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDER = `Paste from Excel, an email or the drawing BOM — one part per line.

759929	Body, Reamer, dbk, valve, 13-3/8 (RM dia 370 mm)	1
767515	O-ring -379 (NBR 90 HI TEMP) SAE AS568	4
Spring, compression, 1.86 OD x 1.63 ID PH Bronze x 2`;

/* Create (first time) or append to an assembly's parts list.
 * Step 1 paste or type → step 2 the preview IS what will be saved (red rows
 * block Save, amber rows are allowed) → step 3 (first time only) who gets ETA
 * alerts. Nothing is written until Save. */
export default function BomCreator({ open, item, currentUser, onClose, onSaved }) {
  const firstTime = !(item?.bom || []).length;
  const [text, setText] = useState("");
  const [sep, setSep] = useState("auto");
  const [quick, setQuick] = useState([]);            // rows added one by one
  const [qNo, setQNo] = useState(""); const [qDesc, setQDesc] = useState(""); const [qQty, setQQty] = useState("");
  const [people, setPeople] = useState([]);
  const [watchers, setWatchers] = useState([]);       // user ids
  const [saving, setSaving] = useState(false);
  const qNoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setText(""); setSep("auto"); setQuick([]); setQNo(""); setQDesc(""); setQQty(""); setWatchers([]);
    if (firstTime) {
      axios.get(`${API}/api/users/mentionables`).then(r => setPeople(r.data || [])).catch(() => setPeople([]));
    }
  }, [open, firstTime]);

  const rows = useMemo(() => {
    const parsed = parsePartsPaste(text, sep, item?.bom || []);
    return parsed.concat(quick.map(q => ({ ...q, flags: [] })));
  }, [text, sep, quick, item]);

  const live = rows.filter(r => !r.skip);
  const bad = live.filter(r => r.flags.some(f => f[0] === "error")).length;
  const warn = live.filter(r => !r.flags.some(f => f[0] === "error") && r.flags.length).length;
  const skipped = rows.length - live.length;
  const canSave = live.length > 0 && bad === 0 && !saving;
  const myId = currentUser?.user_id || currentUser?.id;

  const addQuick = () => {
    const d = qDesc.trim();
    if (!d) return;
    const q = qQty.trim() || "1";
    if (!/^\d+(\.\d+)?$/.test(q) || Number(q) <= 0) { toast.error("Qty must be a number above 0"); return; }
    setQuick(prev => [...prev, { part_no: qNo.trim(), description: d, qty: q }]);
    setQNo(""); setQDesc(""); setQQty("");
    qNoRef.current?.focus();
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = { parts: live.map(r => ({ part_no: r.part_no, description: r.description, qty: Number(r.qty) || 1 })) };
      if (firstTime) body.watchers = watchers;
      const res = await axios.post(`${API}/api/line-items/${item.id}/bom/parts`, body);
      toast.success(`${live.length} child part${live.length === 1 ? "" : "s"} saved`);
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the parts");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !item) return null;
  const pickable = people.filter(p => p.id !== myId && !watchers.includes(p.id));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose?.(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[96vw] md:w-[1000px] max-w-[96vw] p-0 overflow-hidden flex flex-col max-h-[90vh]" data-testid="bom-creator">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-[hsl(var(--border))] shrink-0 text-left">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
            {firstTime ? "New parts list" : "Add more parts"} · {item.po_number} L{item.line_number}
          </div>
          <DialogTitle className="flex items-center gap-2 text-[17px]">
            <AssemblyIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            {item.item_description}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Paste the list or add parts one by one. The preview on the right is exactly what will be saved.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto grid md:grid-cols-[5fr_7fr]">
          {/* left — input */}
          <div className="p-5 flex flex-col gap-3 md:border-r border-[hsl(var(--border))] min-w-0">
            <label htmlFor="bom-paste" className="text-[12.5px] font-semibold flex items-center gap-1.5">
              <ClipboardPaste className="w-4 h-4" /> Paste a list
            </label>
            <textarea
              id="bom-paste"
              autoFocus
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={PLACEHOLDER}
              spellCheck={false}
              className="w-full min-h-[220px] resize-y rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2.5 font-mono-data text-[12px] leading-relaxed placeholder:text-[hsl(var(--muted-foreground))]/55 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
              data-testid="bom-paste"
            />
            <div className="flex items-center gap-2 flex-wrap text-[11.5px] text-[hsl(var(--muted-foreground))]">
              <span>Columns split by</span>
              <div role="radiogroup" aria-label="Column separator" className="inline-flex p-[2px] rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50">
                {[["auto", "Auto"], ["tab", "Tab"], ["comma", "Comma"]].map(([k, l]) => (
                  <button key={k} type="button" role="radio" aria-checked={sep === k} onClick={() => setSep(k)}
                    className={`h-6 px-2.5 rounded-full text-[11px] font-semibold ${sep === k ? "bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-[0_0_0_1px_hsl(var(--border))]" : "hover:text-[hsl(var(--foreground))]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11.5px] text-[hsl(var(--muted-foreground))] leading-snug">
              <b className="text-[hsl(var(--foreground))]">Auto</b> reads Excel columns (part no · description · qty). Without columns it takes the first word as the part number. It never splits a description on its commas.
            </p>

            <div className="border-t border-[hsl(var(--border))] pt-3 flex flex-col gap-2">
              <span className="text-[12.5px] font-semibold">Or add one by one</span>
              <div className="flex gap-1.5">
                <input ref={qNoRef} value={qNo} onChange={e => setQNo(e.target.value)} placeholder="Part no" aria-label="Part no"
                  onKeyDown={e => e.key === "Enter" && addQuick()}
                  className="w-[96px] h-8 px-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[12.5px] font-mono-data focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
                <input value={qDesc} onChange={e => setQDesc(e.target.value)} placeholder="Description" aria-label="Description"
                  onKeyDown={e => e.key === "Enter" && addQuick()}
                  className="flex-1 min-w-0 h-8 px-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[12.5px] focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
                <input value={qQty} onChange={e => setQQty(e.target.value)} placeholder="Qty" aria-label="Qty" inputMode="decimal"
                  onKeyDown={e => e.key === "Enter" && addQuick()}
                  className="w-[52px] h-8 px-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[12.5px] font-mono-data text-right focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
                <button type="button" onClick={addQuick} disabled={!qDesc.trim()}
                  className="h-8 px-3 rounded-md border border-[hsl(var(--border))] text-[12px] font-semibold inline-flex items-center gap-1 hover:bg-[hsl(var(--muted))]/60 disabled:opacity-40 disabled:cursor-not-allowed">
                  <Plus className="w-3.5 h-3.5" /> Add
                </button>
              </div>
            </div>
          </div>

          {/* right — preview */}
          <div className="p-5 flex flex-col gap-3 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[12.5px] font-semibold">Preview — this is what will be saved</span>
              <div className="flex gap-1.5 flex-wrap text-[11px] font-semibold">
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">{live.length - bad} ready</span>
                {warn > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400">{warn} to check</span>}
                {bad > 0 && <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-700 dark:text-red-400">{bad} to fix</span>}
                {skipped > 0 && <span className="px-2 py-0.5 rounded-full border border-dashed border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">{skipped} skipped</span>}
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-[hsl(var(--border))]">
              <table className="w-full text-[12.5px] min-w-[460px]">
                <thead>
                  <tr className="bg-[hsl(var(--muted))]/50 text-[10.5px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    <th className="text-left font-bold px-2.5 py-2 w-8">#</th>
                    <th className="text-left font-bold px-2.5 py-2">Part no</th>
                    <th className="text-left font-bold px-2.5 py-2">Description</th>
                    <th className="text-right font-bold px-2.5 py-2">Qty</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-[12px] text-[hsl(var(--muted-foreground))]">
                      Paste on the left, or add a part one by one.<br />Each line you paste becomes one child part here.
                    </td></tr>
                  )}
                  {(() => {
                    let n = 0; let quickIdx = -1;
                    const parsedCount = rows.length - quick.length;
                    return rows.map((r, i) => {
                      if (r.skip) return (
                        <tr key={i} className="border-t border-[hsl(var(--border))]/60 text-[hsl(var(--muted-foreground))]/70">
                          <td></td>
                          <td colSpan={3} className="px-2.5 py-1.5 line-through truncate max-w-[300px]">{r.raw.trim() || "(blank line)"}</td>
                          <td className="px-2.5 py-1.5 text-[10.5px] whitespace-nowrap no-underline">{r.why}</td>
                        </tr>
                      );
                      n += 1;
                      if (i >= parsedCount) quickIdx += 1;
                      const qi = quickIdx;
                      const err = r.flags.some(f => f[0] === "error");
                      const tint = err ? "bg-red-500/[0.07]" : r.flags.length ? "bg-amber-500/[0.07]" : "";
                      return (
                        <tr key={i} className={`border-t border-[hsl(var(--border))]/60 align-top ${tint}`}>
                          <td className="px-2.5 py-1.5 font-mono-data text-[hsl(var(--muted-foreground))]">{n}</td>
                          <td className="px-2.5 py-1.5 font-mono-data font-semibold whitespace-nowrap">{r.part_no || <span className="text-[hsl(var(--muted-foreground))]/60">—</span>}</td>
                          <td className="px-2.5 py-1.5">
                            {r.description || <span className="text-[hsl(var(--muted-foreground))]/60">—</span>}
                            {r.flags.map((f, k) => (
                              <span key={k} className={`block text-[11px] font-semibold mt-0.5 ${f[0] === "error" ? "text-red-600 dark:text-red-400" : "text-amber-700 dark:text-amber-400"}`}>
                                {f[0] === "error" ? "✕ " : "! "}{f[1]}
                              </span>
                            ))}
                          </td>
                          <td className="px-2.5 py-1.5 text-right font-mono-data">{r.qty}</td>
                          <td className="px-1 py-1">
                            {i >= parsedCount && (
                              <button type="button" aria-label="Remove" title="Remove this part"
                                onClick={() => setQuick(prev => prev.filter((_, k) => k !== qi))}
                                className="w-6 h-6 inline-flex items-center justify-center rounded text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>

            {firstTime && (
              <div className="border-t border-[hsl(var(--border))] pt-3 flex flex-col gap-2">
                <span className="text-[12.5px] font-semibold flex items-center gap-1.5"><BellRing className="w-4 h-4" /> Who gets alerted when an ETA changes</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1.5 h-7 pl-1 pr-3 rounded-full bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))] text-[12px] font-semibold">
                    <Avatar name={currentUser?.name} /> {currentUser?.name || "You"} · you
                  </span>
                  {watchers.map(id => {
                    const p = people.find(x => x.id === id);
                    return (
                      <span key={id} className="inline-flex items-center gap-1.5 h-7 pl-1 pr-1 rounded-full bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))] text-[12px] font-semibold">
                        <Avatar name={p?.name} /> {p?.name || "User"}
                        <button type="button" aria-label={`Remove ${p?.name}`} onClick={() => setWatchers(w => w.filter(x => x !== id))}
                          className="w-5 h-5 inline-flex items-center justify-center rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                  <select
                    value=""
                    onChange={e => e.target.value && setWatchers(w => [...w, e.target.value])}
                    aria-label="Add a person to ETA alerts"
                    className="h-7 px-2 rounded-full border border-dashed border-[hsl(var(--border))] bg-transparent text-[12px] text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500/40"
                  >
                    <option value="">+ Add person…</option>
                    {pickable.map(p => <option key={p.id} value={p.id}>{p.name || p.email}{p.role ? ` · ${p.role}` : ""}</option>)}
                  </select>
                </div>
                <span className="text-[11.5px] text-[hsl(var(--muted-foreground))]">You're included because you're making the list. They get a bell notification and an email. You can change this later from the tracker.</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 shrink-0 flex-wrap">
          <span className="text-[11.5px] text-[hsl(var(--muted-foreground))]">
            {bad ? "Fix the red rows in the paste box — amber rows are allowed." : "You can edit or remove parts later from the tracker until they're ordered."}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={saving} className="h-8 px-3.5 rounded-lg text-[12.5px] font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/60">Cancel</button>
            <button type="button" onClick={save} disabled={!canSave}
              className="h-8 px-4 rounded-lg text-[12.5px] font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-45 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="bom-save">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {bad ? `Fix ${bad} row${bad === 1 ? "" : "s"} to save` : live.length ? `Save ${live.length} part${live.length === 1 ? "" : "s"}` : "Save parts"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function Avatar({ name }) {
  const ini = (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  return (
    <span className="w-5 h-5 rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-300 text-[8.5px] font-extrabold inline-flex items-center justify-center shrink-0">{ini}</span>
  );
}
